import type { Doc } from '~/convex/_generated/dataModel';

type ReportStatus = Doc<'reports'>['status'];

export type ReportStatusBadgeConfig = {
  labelKey: string;
  variant: 'gray' | 'info' | 'warning' | 'destructive' | 'success';
};

export const REPORT_STATUS_BADGE_BY_STATUS = {
  draft: { labelKey: 'report:status.draft', variant: 'gray' },
  recording: { labelKey: 'report:status.recording', variant: 'gray' },
  transcribing: { labelKey: 'report:status.transcribing', variant: 'gray' },
  processing: { labelKey: 'report:status.processing', variant: 'info' },
  generating: { labelKey: 'report:status.generating', variant: 'info' },
  completed: { labelKey: 'report:status.completed', variant: 'success' },
  error: { labelKey: 'report:status.error', variant: 'destructive' },
} as const satisfies Record<ReportStatus, ReportStatusBadgeConfig>;

const FALLBACK_BADGE: ReportStatusBadgeConfig = {
  labelKey: 'report:status.draft',
  variant: 'gray',
};

export const getReportStatusBadgeConfig = (
  status: ReportStatus,
): ReportStatusBadgeConfig =>
  REPORT_STATUS_BADGE_BY_STATUS[
    status as keyof typeof REPORT_STATUS_BADGE_BY_STATUS
  ] ?? FALLBACK_BADGE;

export type ReportRouteSegment = 'record' | 'processing' | 'edit';

export const REPORT_ROUTE_SEGMENT_BY_STATUS = {
  draft: 'record',
  recording: 'record',
  transcribing: 'processing',
  processing: 'processing',
  generating: 'processing',
  completed: 'edit',
  error: 'processing',
} as const satisfies Record<ReportStatus, ReportRouteSegment>;

export const getReportRouteSegment = (
  status: ReportStatus,
): ReportRouteSegment =>
  REPORT_ROUTE_SEGMENT_BY_STATUS[
    status as keyof typeof REPORT_ROUTE_SEGMENT_BY_STATUS
  ] ?? 'record';

const TRANSIENT_REPORT_STATUSES: ReadonlySet<ReportStatus> = new Set([
  'recording',
  'transcribing',
  'processing',
  'generating',
]);

export const isTransientStatus = (status: ReportStatus): boolean =>
  TRANSIENT_REPORT_STATUSES.has(status);
